import React from 'react';
import { MapPin } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const MapUserLocationNotice = ({
  isLocating,
  isLocationNearMap = true,
  locationError,
  onRequestLocation,
  permissionState,
  userLocation
}) => {
  const { t } = useTranslation();
  const hasLocation = Boolean(userLocation);
  const isUnsupported = permissionState === 'unsupported';
  const shouldShowAction = !isUnsupported && typeof onRequestLocation === 'function';

  const title = hasLocation
    ? t('interactiveMap.locationPrompt.activeTitle', 'Votre position est prête')
    : t('interactiveMap.locationPrompt.title', 'Afficher ma position sur la carte');

  let description = t(
    'interactiveMap.locationPrompt.description',
    'Autorisez la localisation pour voir un point bleu sur la carte et mieux vous repérer dans le parc.'
  );

  if (hasLocation && isLocationNearMap) {
    description = t(
      'interactiveMap.locationPrompt.activeDescription',
      'Votre position est affichée en bleu pour vous aider à vous orienter plus facilement.'
    );
  }

  if (hasLocation && !isLocationNearMap) {
    description = t(
      'interactiveMap.locationPrompt.outsideMapDescription',
      'Votre position a bien été détectée, mais elle semble en dehors de la zone affichée par cette carte.'
    );
  }

  const actionLabel = hasLocation
    ? t('interactiveMap.locationPrompt.refresh', 'Actualiser ma position')
    : locationError || permissionState === 'denied'
      ? t('interactiveMap.locationPrompt.retry', 'Réessayer')
      : t('interactiveMap.locationPrompt.enable', 'Afficher ma position');

  return (
    <div className={`map-location-notice${hasLocation ? ' is-active' : ''}${locationError ? ' is-error' : ''}`}>
      <div className="map-location-notice-copy">
        <div className="map-location-notice-title">
          <MapPin size={16} />
          <strong>{title}</strong>
        </div>
        <p>{description}</p>
        {!hasLocation && (
          <p className="map-location-notice-privacy">
            {t(
              'interactiveMap.locationPrompt.privacy',
              'Nous demandons cette autorisation uniquement pour l’affichage local de votre position sur la carte.'
            )}
          </p>
        )}
        {locationError && (
          <p className="map-location-notice-error" role="alert">
            {locationError}
          </p>
        )}
        {!locationError && permissionState === 'denied' && (
          <p className="map-location-notice-error" role="alert">
            {t(
              'errors.geolocation.denied',
              'L’accès à votre position a été refusé. Autorisez la géolocalisation dans les paramètres de votre navigateur.'
            )}
          </p>
        )}
        {!locationError && isUnsupported && (
          <p className="map-location-notice-error" role="alert">
            {t('errors.geolocation.notSupported', 'La géolocalisation n\'est pas supportée par votre navigateur')}
          </p>
        )}
      </div>

      {shouldShowAction && (
        <button
          type="button"
          className="map-location-notice-button"
          onClick={onRequestLocation}
          disabled={isLocating}
        >
          {isLocating
            ? t('interactiveMap.locationPrompt.loading', 'Localisation en cours...')
            : actionLabel}
        </button>
      )}
    </div>
  );
};

export default MapUserLocationNotice;
